import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useEnquiry } from "../context/EnquiryContext";
import { submitConsultation, submitQuote } from "../lib/api";

const Field = ({ label, name, value, onChange, error, testid, type = "text", textarea, placeholder }) => (
  <div className="flex flex-col">
    <label className="text-[11px] font-semibold tracking-[0.18em] uppercase text-navy/50 mb-2">
      {label}
    </label>
    {textarea ? (
      <textarea
        data-testid={testid}
        name={name}
        rows={3}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-transparent border-b border-navy/20 rounded-none px-0 py-2 text-navy placeholder:text-navy/30 focus:outline-none focus:border-terracotta transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={testid}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-transparent border-b border-navy/20 rounded-none px-0 py-2 text-navy placeholder:text-navy/30 focus:outline-none focus:border-terracotta transition-colors"
      />
    )}
    {error && <span className="text-terracotta text-xs mt-1.5">{error}</span>}
  </div>
);

export default function EnquiryModal() {
  const { open, mode, source, closeEnquiry } = useEnquiry();
  const isQuote = mode === "quote";

  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({});
      setErrors({});
      setDone(false);
    }
  }, [open, mode]);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Please enter your name";
    if (!form.email?.trim()) e.email = "Email required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.pincode?.trim()) e.pincode = "Pincode required";
    if (isQuote) {
      if (!form.address?.trim()) e.address = "Address required";
      if (!form.requirement?.trim()) e.requirement = "Tell us what you need";
      if (!form.budget?.trim()) e.budget = "Budget required";
    } else {
      if (!form.mobile?.trim()) e.mobile = "Mobile number required";
      else if (!/^[0-9+\-\s]{6,15}$/.test(form.mobile.trim())) e.mobile = "Enter a valid number";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (isQuote) {
        await submitQuote({
          name: form.name.trim(),
          mobile: form.mobile?.trim() || null,
          email: form.email.trim(),
          address: form.address.trim(),
          pincode: form.pincode.trim(),
          requirement: form.requirement.trim(),
          budget: form.budget.trim(),
          source: source || "quote",
        });
      } else {
        await submitConsultation({
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          email: form.email.trim(),
          pincode: form.pincode.trim(),
          source: source || "consultation",
        });
      }
      setDone(true);
      toast.success("Thank you — our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="enquiry-modal"
        >
          <div
            className="absolute inset-0 bg-navy/60 backdrop-blur-sm"
            onClick={closeEnquiry}
          />
          <motion.div
            className="relative w-full max-w-3xl bg-warm border border-navy/10 shadow-2xl overflow-hidden"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            <button
              data-testid="enquiry-close-btn"
              onClick={closeEnquiry}
              className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center border border-navy/15 text-navy hover:bg-navy hover:text-warm transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="grid md:grid-cols-[0.9fr_1.1fr]">
              {/* Left brand rail */}
              <div className="hidden md:flex flex-col justify-between bg-navy text-warm p-8">
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-terracotta">DIDIT</p>
                  <h3 className="font-display text-3xl font-light mt-6 leading-tight">
                    {isQuote ? "Get your interior estimate" : "Free design consultation"}
                  </h3>
                  <p className="text-warm/60 text-sm mt-4 leading-relaxed">
                    {isQuote
                      ? "Share a few details and our designers will prepare a tailored estimate for your home."
                      : "Tell us how to reach you. A DIDIT designer will call to understand your space."}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-warm/50 pt-8">
                  <span>Design</span><span className="text-terracotta">/</span>
                  <span>Install</span><span className="text-terracotta">/</span>
                  <span>Deliver</span>
                </div>
              </div>

              {/* Right form */}
              <div className="p-8 md:p-10">
                {done ? (
                  <div className="flex flex-col items-start justify-center h-full py-10" data-testid="enquiry-success">
                    <div className="w-14 h-14 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta mb-6">
                      <Check size={26} />
                    </div>
                    <h3 className="font-display text-2xl text-navy font-light">Enquiry received</h3>
                    <p className="text-ink/70 mt-3 text-sm leading-relaxed">
                      Thank you, {form.name?.split(" ")[0] || "there"}. Our team will be in touch shortly, and a confirmation email is on its way to your inbox.
                    </p>
                    <button
                      onClick={closeEnquiry}
                      data-testid="enquiry-success-close"
                      className="mt-8 rounded-full bg-navy text-warm px-7 py-3 text-sm font-medium hover:bg-terracotta transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5" data-testid="enquiry-form">
                    <div className="md:hidden mb-2">
                      <p className="text-[11px] tracking-[0.3em] uppercase text-terracotta">DIDIT</p>
                      <h3 className="font-display text-2xl font-light text-navy mt-2">
                        {isQuote ? "Get your estimate" : "Free consultation"}
                      </h3>
                    </div>

                    <Field label="Name" name="name" value={form.name || ""} onChange={onChange} error={errors.name} testid="field-name" placeholder="Your full name" />

                    {isQuote ? (
                      <>
                        <Field label="Mobile Number" name="mobile" value={form.mobile || ""} onChange={onChange} error={errors.mobile} testid="field-mobile" placeholder="Optional" />
                        <Field label="Email" name="email" type="email" value={form.email || ""} onChange={onChange} error={errors.email} testid="field-email" placeholder="you@example.com" />
                        <Field label="Address" name="address" value={form.address || ""} onChange={onChange} error={errors.address} testid="field-address" placeholder="Where is the project?" />
                        <div className="grid grid-cols-2 gap-5">
                          <Field label="Pincode" name="pincode" value={form.pincode || ""} onChange={onChange} error={errors.pincode} testid="field-pincode" placeholder="e.g. 560001" />
                          <Field label="Budget" name="budget" value={form.budget || ""} onChange={onChange} error={errors.budget} testid="field-budget" placeholder="e.g. ₹8–12L" />
                        </div>
                        <Field label="Requirements" name="requirement" value={form.requirement || ""} onChange={onChange} error={errors.requirement} testid="field-requirement" textarea placeholder="Kitchen, wardrobes, full home..." />
                      </>
                    ) : (
                      <>
                        <Field label="Mobile Number" name="mobile" value={form.mobile || ""} onChange={onChange} error={errors.mobile} testid="field-mobile" placeholder="We'll call you back" />
                        <Field label="Email" name="email" type="email" value={form.email || ""} onChange={onChange} error={errors.email} testid="field-email" placeholder="you@example.com" />
                        <Field label="Pincode" name="pincode" value={form.pincode || ""} onChange={onChange} error={errors.pincode} testid="field-pincode" placeholder="e.g. 560001" />
                      </>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="enquiry-submit-btn"
                      className="group w-full flex items-center justify-center gap-2 rounded-full bg-navy text-warm px-8 py-4 text-sm font-medium tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60"
                    >
                      {loading ? "Sending..." : isQuote ? "Get My Quote" : "Request Consultation"}
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <p className="text-[11px] text-ink/50 leading-relaxed">
                      By submitting, you agree to be contacted by DIDIT regarding your enquiry.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
